import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import Colors from "../../../ui/global/Colors";
import LoadingSpinner from "../../../ui/components/LoadingSpinner";
import { motion } from "framer-motion";

const MiniViewContainer = styled(motion.div)<{ $isLoading: boolean, $size: number }>`
    position: absolute;
    top: 6px;
    right: 6px;
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    border-radius: 4px;
    border: 1px solid ${Colors.grey2};
    background-color: ${Colors.white};
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    cursor: pointer;
    opacity: ${props => props.$isLoading ? 0 : 1};
`;

const LoadingSpinnerContainer = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 40px;
    height: 40px;
    padding: 0;
    z-index: 10;
`;

interface Mini3DViewProps {
    model3D: string;
    onClick?: () => void;
    miniSize: number;
}

const Mini3DView: React.FC<Mini3DViewProps> = ({ model3D, onClick, miniSize }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const modelRef = useRef<THREE.Object3D | null>(null);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string | null>(null);

    // Setup mini 3D view
    useEffect(() => {
        if (!containerRef.current || !model3D) return;

        // Create renderer
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(miniSize, miniSize);
        renderer.setPixelRatio(window.devicePixelRatio);
        containerRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Create scene
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf0f0f0);

        // Create camera - positioned for front view
        const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
        camera.position.set(0, 2, 12); // Front view position
        camera.lookAt(0, 2, 0); // Look at center of model

        // Add lights
        const keyLight = new THREE.DirectionalLight(0xffffff, 6.0);
        keyLight.position.set(3, 5, 5);
        scene.add(keyLight);
        
        const ambientLight = new THREE.AmbientLight(0xffffff, 10.0);
        scene.add(ambientLight);

        const fillLight = new THREE.DirectionalLight(0xffffff, 8.0); 
        fillLight.position.set(3, 2, 5);
        scene.add(fillLight);

        // Add grid helper
        const gridHelper = new THREE.GridHelper(10, 10);
        gridHelper.position.y = -1;
        scene.add(gridHelper);

        // Load 3D model
        const loader = new GLTFLoader();
        setIsLoading(true);
        
        loader.load(
            model3D,
            (gltf) => {
                const model = gltf.scene;
                
                // Calculate initial model dimensions
                const initialBox = new THREE.Box3().setFromObject(model);
                const center = initialBox.getCenter(new THREE.Vector3());
                const size = initialBox.getSize(new THREE.Vector3());
                
                // Scale model to fill more of the viewport
                const maxDim = Math.max(size.x, size.y, size.z);
                const scale = 7 / maxDim;
                model.scale.set(scale, scale, scale);

                // Recalculate bounding box after scaling
                const scaledBox = new THREE.Box3().setFromObject(model);
                
                // Reset position to center in all dimensions but place on ground
                model.position.x = -center.x * scale;
                model.position.y = -scaledBox.min.y - 1; // Place bottom on grid
                model.position.z = -center.z * scale;
                
                scene.add(model);
                modelRef.current = model;
                
                // Render the scene
                renderer.render(scene, camera);
                setIsLoading(false);
            },
            undefined,
            (error) => {
                console.error('Error loading model:', error);
                setError('Failed to load 3D model');
                setIsLoading(false);
            }
        );

        // Cleanup function
        return () => {
            if (rendererRef.current && containerRef.current) {
                containerRef.current.removeChild(rendererRef.current.domElement);
                rendererRef.current.dispose();
                rendererRef.current = null;
            }
            
            // Dispose of scene resources
            if (scene) {
                scene.traverse((object) => {
                    if (object instanceof THREE.Mesh) {
                        if (object.geometry) object.geometry.dispose();
                        
                        if (object.material) {
                            if (Array.isArray(object.material)) {
                                object.material.forEach(material => material.dispose());
                            } else {
                                object.material.dispose();
                            }
                        }
                    }
                });
            }
        };
    }, [model3D]);

    return (
        <MiniViewContainer 
            ref={containerRef} 
            $size={miniSize}
            onClick={onClick}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            $isLoading={isLoading}
        />
    );
};

export default Mini3DView;